import { useState, useMemo, useEffect } from "react";
import toast from "react-hot-toast";
import type {
  ArcgisLayerConfig,
  AssetFieldConfig,
  LayerFormData,
} from "@/types/layers";
import {
  TbPlus,
  TbEdit,
  TbTrash,
  TbSearch,
  TbMap,
  TbGripVertical,
} from "react-icons/tb";
import * as Label from "@radix-ui/react-label";
import { useLayerStore } from "@/stores/layerStore";
import { useModalStore } from "@/stores/modalStore";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { LayerService } from "@/services/layerService";
import { ActivityLogService } from "@/services/activityLogService";
import { useTenant } from "@/hooks/useTenant";
import { useAuth } from "@/hooks/useAuth";
import { useMapContext } from "@/hooks/useMap";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import type { PointerEvent } from "react";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  DEFAULT_TASK_LAYER_COLOR,
  TASK_LAYER_KEY,
  TASK_LAYER_LABEL,
} from "@/constant/layers";

// Custom PointerSensor that ignores elements with data-no-dnd attribute
class SmartPointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: "onPointerDown" as const,
      handler: ({ nativeEvent: event }: PointerEvent): boolean => {
        // Check if the clicked element or any of its ancestors has data-no-dnd
        let element = event.target as HTMLElement | null;
        while (element) {
          if (element.dataset?.noDnd === "true") {
            return false; // Don't activate drag
          }
          element = element.parentElement;
        }

        return true; // Activate drag
      },
    },
  ];
}

interface SortableLayerItemProps {
  layer: ArcgisLayerConfig;
  onEdit: (layer: ArcgisLayerConfig) => void;
  onDelete: (key: string) => void;
  isAdmin: boolean;
  onColorChange: (layer: ArcgisLayerConfig, color: string) => void;
  isColorSaving: boolean;
}

// Hoisted to module scope so its identity is stable across LayerManager
// re-renders. Defining it inside the parent caused unmount + remount on every
// query refetch, which wiped local state (e.g. `isEditingColor`) and lost
// scroll position whenever a mutation invalidated the layers cache.
const SortableLayerItem: React.FC<SortableLayerItemProps> = ({
  layer,
  onEdit,
  onDelete,
  isAdmin,
  onColorChange,
  isColorSaving,
}) => {
  const isTaskLayer = layer.key === TASK_LAYER_KEY;
  const [isEditingColor, setIsEditingColor] = useState(false);
  const [draftColor, setDraftColor] = useState<string | null>(null);
  const currentColor = layer.color || DEFAULT_TASK_LAYER_COLOR;
  const displayedColor = draftColor ?? currentColor;
  // <input type="color"> emits lowercase hex; the DB stores uppercase. Compare
  // case-insensitively so picking the same color doesn't flag as unsaved.
  const hasUnsavedColorChanges =
    draftColor !== null &&
    draftColor.toLowerCase() !== currentColor.toLowerCase();

  const handleColorSave = () => {
    if (!hasUnsavedColorChanges || !draftColor) return;
    onColorChange(layer, draftColor);
    setDraftColor(null);
    setIsEditingColor(false);
  };

  const handleColorCancel = () => {
    setDraftColor(null);
    setIsEditingColor(false);
  };

  const handleColorEditToggle = () => {
    if (isEditingColor) setDraftColor(null);
    setIsEditingColor((prev) => !prev);
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: layer.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex justify-between items-start self-stretch p-4 rounded-2xl border border-[#F3F4F6] outline-1 outline-[#F3F4F6] -outline-offset-1 hover:bg-gray-50 transition-colors"
    >
      <div className="flex flex-col w-full justify-start items-start gap-3">
        <div className="flex flex-col w-full justify-start items-start gap-2">
          <div className="flex flex-row justify-between w-full items-center gap-2.5">
            <div className="flex justify-start items-center gap-2.5">
              {isAdmin && (
                <div
                  {...attributes}
                  {...listeners}
                  className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 rounded"
                  title="Drag to reorder"
                >
                  <TbGripVertical className="w-4 h-4 text-gray-400" />
                </div>
              )}
              <TbMap className="w-5 h-5 text-[#374795]" />
              <div className="text-[#444444] text-[16px] font-['Open_Sans'] font-bold leading-[22.4px]">
                {isTaskLayer ? `${TASK_LAYER_LABEL} (System)` : layer.label}
              </div>
              <div
                className={`px-2.5 py-[3px] pb-[5px] pl-2.5 pr-2.5 rounded-[20px] flex justify-center items-center gap-2 ${
                  layer.visible
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                <div className="text-[12px] font-['Open_Sans'] font-semibold leading-[15.6px]">
                  {layer.visible ? "Visible" : "Hidden"}
                </div>
              </div>
              {layer.task_enabled && (
                <div className="px-2.5 py-[3px] pb-[5px] pl-2.5 pr-2.5 bg-blue-100 text-blue-800 rounded-[20px] flex justify-center items-center gap-2">
                  <div className="text-center text-[12px] font-['Open_Sans'] font-semibold leading-[15.6px]">
                    Tasks Enabled
                  </div>
                </div>
              )}
              <div
                className={`px-2.5 py-[3px] pb-[5px] pl-2.5 pr-2.5 rounded-[20px] flex justify-center items-center gap-2 ${
                  layer.service_type === "featureservice"
                    ? "bg-purple-100 text-purple-800"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                <div className="text-center text-[12px] font-['Open_Sans'] font-semibold leading-[15.6px]">
                  {layer.service_type === "featureservice"
                    ? "Feature Service"
                    : "Map Service"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2.5" data-no-dnd="true">
              {isAdmin && !isTaskLayer && (
                <div className="flex justify-end items-start gap-2 shrink-0">
                  <button
                    type="button"
                    data-no-dnd="true"
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(layer);
                    }}
                    className="w-9 h-9 rounded-full border border-[#F3F4F6] bg-white flex items-center cursor-pointer justify-center hover:bg-gray-50 transition-colors relative z-50"
                    title="Edit Layer"
                  >
                    <TbEdit
                      size={14}
                      className="text-[#828282]"
                      style={{ pointerEvents: "none" }}
                    />
                  </button>
                  <button
                    type="button"
                    data-no-dnd="true"
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(layer.key);
                    }}
                    className="w-9 h-9 rounded-full border border-[#F3F4F6] bg-white flex items-center cursor-pointer justify-center hover:bg-red-50 transition-colors relative z-50"
                    title="Delete Layer"
                  >
                    <TbTrash
                      size={14}
                      className="text-red-500"
                      style={{ pointerEvents: "none" }}
                    />
                  </button>
                </div>
              )}
              {isAdmin && isTaskLayer && (
                <div className="flex justify-end items-start gap-2 shrink-0">
                  <button
                    type="button"
                    data-no-dnd="true"
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleColorEditToggle();
                    }}
                    className={`w-9 h-9 rounded-full border border-[#F3F4F6] flex items-center cursor-pointer justify-center transition-colors relative z-50 ${
                      isEditingColor
                        ? "bg-[rgba(55,71,149,0.1)]"
                        : "bg-white hover:bg-gray-50"
                    }`}
                    title={
                      isEditingColor
                        ? "Close color editor"
                        : "Edit task layer color"
                    }
                  >
                    <TbEdit
                      size={14}
                      className={
                        isEditingColor ? "text-[#374795]" : "text-[#828282]"
                      }
                      style={{ pointerEvents: "none" }}
                    />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col w-full justify-start items-start gap-1">
          <div className="text-[#828282] text-[12px] font-['Open_Sans'] font-medium leading-[15.6px]">
            Service URL
          </div>
          <div className="text-[#444444] text-[12px] font-['Open_Sans'] font-medium leading-[15.6px] break-all">
            {layer.url}
          </div>
        </div>

        {isTaskLayer && isAdmin && isEditingColor && (
          <div
            className="flex flex-col w-full justify-start items-start gap-2"
            data-no-dnd="true"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div className="text-[#828282] text-[12px] font-['Open_Sans'] font-medium leading-[15.6px]">
              Feature Color
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="color"
                value={displayedColor}
                disabled={isColorSaving}
                onChange={(e) => setDraftColor(e.target.value)}
                className="w-9 h-9 rounded border border-[#F3F4F6] cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                title="Task feature color"
              />
              <span className="text-[#444444] text-[12px] font-['Open_Sans'] font-medium leading-[15.6px] uppercase">
                {displayedColor}
              </span>
              {hasUnsavedColorChanges && !isColorSaving && (
                <span className="text-[#828282] text-[12px] font-['Open_Sans'] italic">
                  unsaved
                </span>
              )}
              {isColorSaving && (
                <span className="text-[#828282] text-[12px] font-['Open_Sans']">
                  Saving…
                </span>
              )}
              <button
                type="button"
                onClick={handleColorSave}
                disabled={!hasUnsavedColorChanges || isColorSaving}
                className="px-3 py-1.5 rounded-lg text-[12px] font-semibold font-['Open_Sans'] text-white bg-[#374795] hover:bg-[#132477] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Save
              </button>
              <button
                type="button"
                onClick={handleColorCancel}
                disabled={isColorSaving}
                className="px-3 py-1.5 rounded-lg text-[12px] font-semibold font-['Open_Sans'] text-[#444444] bg-white border border-[#F3F4F6] hover:bg-gray-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {layer.service_type === "mapservice" &&
          layer.layer_numbers &&
          layer.layer_numbers.length > 0 && (
            <div className="flex flex-col w-full justify-start items-start gap-1">
              <div className="text-[#828282] text-[12px] font-['Open_Sans'] font-medium leading-[15.6px]">
                Layer Numbers
              </div>
              <div className="flex justify-start items-center gap-1 flex-wrap">
                {layer.layer_numbers.map((num) => (
                  <div
                    key={`layer-num-${num}`}
                    className="px-2 py-0.5 bg-[#374795] text-white rounded text-[10px] font-['Open_Sans'] font-medium"
                  >
                    {num}
                  </div>
                ))}
              </div>
            </div>
          )}

        {layer.asset_fields && layer.asset_fields.length > 0 && (
          <div className="flex flex-col w-full justify-start items-start gap-1">
            <div className="text-[#828282] text-[12px] font-['Open_Sans'] font-medium leading-[15.6px]">
              Asset Fields
            </div>
            <div className="flex justify-start items-center gap-1 flex-wrap">
              {layer.asset_fields.map((field) => (
                <div
                  key={`asset-field-${field.field}-${field.property_key}`}
                  className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-[10px] font-['Open_Sans'] font-medium"
                  title={`Field: ${field.field}, Property: ${field.property_key}`}
                >
                  {field.label}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface LayerManagerProps {
  error?: string;
}

export const LayerManager: React.FC<LayerManagerProps> = ({ error }) => {
  // Auth context for role checking
  const { isAdmin } = useAuth();
  const { tenantId } = useTenant();

  // Map context for refreshing layers
  const { refreshLayers } = useMapContext();

  // Zustand store
  const {
    newLayerData,
    validationError,
    setNewLayerData,
    setValidationError,
    startCreateLayer,
    startEditLayer,
    resetLayerForm,
    validateLayerData,
    addAssetField,
    removeAssetField,
    updateAssetField,
  } = useLayerStore();

  // Modal state from modalStore
  const {
    showCreateLayer,
    editingLayer,
    openEditLayer,
    closeCreateLayer,
    closeEditLayer,
  } = useModalStore();

  // Local state for search
  const [searchTerm, setSearchTerm] = useState("");

  // Wrapper function to check admin permissions before creating
  const handleStartCreateLayer = () => {
    if (!isAdmin) {
      toast.error("Only administrators can create layers.");
      return;
    }
    startCreateLayer();
  };

  // Query client for cache invalidation
  const queryClient = useQueryClient();

  // Fetch layers
  const {
    data: layers = [],
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ["asset-layers", tenantId],
    queryFn: () => LayerService.getAllLayers(tenantId!),
    enabled: !!tenantId,
  });

  // Create layer mutation
  const createLayerMutation = useMutation({
    mutationFn: (data: LayerFormData) =>
      LayerService.createLayer(data, tenantId!),
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: ["asset-layers", tenantId],
      });
      resetLayerForm();
      closeCreateLayer();
      // Refresh map layers to reflect new layer
      try {
        await refreshLayers();
      } catch (error) {
        console.error("Failed to refresh map layers:", error);
      }
    },
    onError: (error) => {
      console.error("Failed to create layer:", error);
      setValidationError("Failed to create layer. Please try again.");
    },
  });

  // Update layer mutation
  const updateLayerMutation = useMutation({
    mutationFn: ({
      key,
      data,
    }: {
      key: string;
      data: Partial<LayerFormData>;
    }) => LayerService.updateLayer(key, data, tenantId!),
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: ["asset-layers", tenantId],
      });
      resetLayerForm();
      closeEditLayer();
      // Refresh map layers to reflect changes
      try {
        await refreshLayers();
      } catch (error) {
        console.error("Failed to refresh map layers:", error);
      }
    },
    onError: (error) => {
      console.error("Failed to update layer:", error);
      setValidationError("Failed to update layer. Please try again.");
    },
  });

  // Delete layer mutation
  const deleteLayerMutation = useMutation({
    mutationFn: (key: string) => LayerService.deleteLayer(key, tenantId!),
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: ["asset-layers", tenantId],
      });
      try {
        await refreshLayers();
      } catch (error) {
        console.error("Failed to refresh map layers:", error);
      }
    },
    onError: (error) => {
      console.error("Failed to delete layer:", error);
    },
  });

  // Update layer order mutation
  const updateLayerOrderMutation = useMutation({
    mutationFn: (layerOrders: { key: string; display_order: number }[]) =>
      LayerService.updateLayersOrder(layerOrders, tenantId!),
    onSuccess: async () => {
      // Invalidate React Query cache
      queryClient.invalidateQueries({
        queryKey: ["asset-layers", tenantId],
      });

      // Refresh map layers to reflect new order
      try {
        await refreshLayers();
      } catch (error) {
        console.error("Failed to refresh map layers:", error);
      }
    },
    onError: (error) => {
      console.error("Failed to update layer order:", error);
    },
  });

  // Drag and drop sensors
  // Use SmartPointerSensor which ignores elements with data-no-dnd attribute
  // This allows edit/delete buttons to work without triggering drag
  const sensors = useSensors(
    useSensor(SmartPointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    // Debug log removed

    if (active.id !== over?.id) {
      const oldIndex = filteredLayers.findIndex(
        (layer) => layer.key === active.id
      );
      const newIndex = filteredLayers.findIndex(
        (layer) => layer.key === over?.id
      );

      // Debug log removed

      const reorderedLayers = arrayMove(filteredLayers, oldIndex, newIndex);

      // Update display_order for all affected layers
      const layerOrders = reorderedLayers.map((layer, index) => ({
        key: layer.key,
        display_order: index,
      }));

      // Debug log removed
      updateLayerOrderMutation.mutate(layerOrders);
    }
  };


  const filteredLayers = useMemo(() => {
    if (!searchTerm) {
      return layers;
    }
    return layers.filter(
      (layer) =>
        layer.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        layer.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
        layer.key.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [layers, searchTerm]);

  const handleSubmit = async () => {
    // Check if user is admin before allowing any modifications
    if (!isAdmin) {
      toast.error("Only administrators can modify layers.");
      return;
    }

    // Validate layer data before submission
    if (!validateLayerData()) {
      return; // Stop submission if validation fails
    }

    if (editingLayer) {
      await updateLayerMutation.mutateAsync({
        key: editingLayer.key,
        data: {
          label: newLayerData.label,
          url: newLayerData.url,
          layer_numbers: newLayerData.layer_numbers,
          visible: newLayerData.visible,
          task_enabled: newLayerData.task_enabled,
          asset_fields:
            newLayerData.asset_fields.length > 0
              ? newLayerData.asset_fields
              : undefined,
          skip_asset_allowed: newLayerData.skip_asset_allowed,
          service_type: newLayerData.service_type,
        },
      });
    } else {
      // For creating a new layer, create the form data object properly
      const createData: LayerFormData = {
        key: newLayerData.key,
        label: newLayerData.label,
        url: newLayerData.url,
        layer_numbers: newLayerData.layer_numbers,
        visible: newLayerData.visible,
        task_enabled: newLayerData.task_enabled,
        skip_asset_allowed: newLayerData.skip_asset_allowed,
        service_type: newLayerData.service_type,
      };

      // Only add asset_fields if they exist
      if (newLayerData.asset_fields && newLayerData.asset_fields.length > 0) {
        createData.asset_fields = newLayerData.asset_fields;
      }

      await createLayerMutation.mutateAsync(createData);
    }
  };

  const handleEdit = (layer: ArcgisLayerConfig) => {
    if (!isAdmin) {
      toast.error("Only administrators can edit layers.");
      return;
    }
    // Populate the form with existing layer data
    startEditLayer(layer);
    // Show the edit modal
    openEditLayer(layer);
  };

  const handleDelete = (layerKey: string) => {
    if (!isAdmin) {
      toast.error("Only administrators can delete layers.");
      return;
    }
    if (
      window.confirm(
        "Are you sure you want to delete this layer? This action cannot be undone."
      )
    ) {
      deleteLayerMutation.mutate(layerKey);
    }
  };

  // Ensure the reserved Task Layer exists in DB (admin-only)
  useEffect(() => {
    if (!isAdmin) return;
    if (isLoading) return;
    const hasTaskLayer = layers?.some((l) => l.key === TASK_LAYER_KEY);
    if (!hasTaskLayer) {
      // Create a minimal placeholder row so its display_order can be managed by admin
      const createTaskLayer = async () => {
        try {
          await createLayerMutation.mutateAsync({
            key: TASK_LAYER_KEY,
            label: TASK_LAYER_LABEL,
            url: "system://task-layer",
            layer_numbers: [],
            visible: true,
            task_enabled: false,
            skip_asset_allowed: true,
            service_type: "mapservice",
            display_order: 0, // By default on top; admin can reorder
          });
          await queryClient.invalidateQueries({ queryKey: ["asset-layers"] });
        } catch (e) {
          console.warn("Could not auto-create Task Layer:", e);
        }
      };
      createTaskLayer();
    }
  }, [isAdmin, isLoading, layers, createLayerMutation, queryClient]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-[#828282] text-[14px] font-medium">
          Loading layers...
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Full Screen Create/Edit Layer Form */}
      {showCreateLayer || editingLayer ? (
        <>
          <div className="flex flex-col items-start w-full overflow-y-auto min-h-[300px] md:min-h-[400px] max-h-[calc(100vh-250px)] md:max-h-[calc(100vh-380px)] p-4 md:p-6 rounded-[10px] border border-gray-100">
            <div className="space-y-6 mb-8 w-full">
              <div>
                <Label.Root
                  htmlFor="layer-key"
                  className="inline-flex gap-1 text-[14px] font-medium text-[#444444] mb-3"
                >
                  Layer Key <span className="text-red-500">*</span>
                </Label.Root>
                <div className="bg-gray-100 rounded-[10px] px-4 py-3">
                  <input
                    id="layer-key"
                    type="text"
                    value={newLayerData.key}
                    onChange={(e) => {
                      setNewLayerData({
                        ...newLayerData,
                        key: e.target.value,
                      });
                      // Clear validation error when user starts typing
                      if (validationError) {
                        setValidationError(null);
                      }
                    }}
                    className="bg-transparent text-[14px] text-[#444444] font-medium leading-normal placeholder-[#828282] border-none outline-none w-full"
                    placeholder="Enter unique layer key (e.g., bridge_inventory)"
                    disabled={!!editingLayer} // Can't edit key for existing layers
                    maxLength={50}
                  />
                </div>
                <div className="flex justify-between items-center mt-1">
                  {validationError ? (
                    <div className="text-red-500 text-[12px] font-medium">
                      {validationError}
                    </div>
                  ) : (
                    <div />
                  )}
                  <span
                    className={`text-[12px] font-medium ${newLayerData.key.length >= 50 ? "text-red-500" : "text-[#828282]"}`}
                  >
                    {newLayerData.key.length}/50
                  </span>
                </div>
              </div>

              <div>
                <Label.Root
                  htmlFor="layer-label"
                  className="inline-flex gap-1 text-[14px] font-medium text-[#444444] mb-3"
                >
                  Layer Name <span className="text-red-500">*</span>
                </Label.Root>
                <div className="bg-gray-100 rounded-[10px] px-4 py-3">
                  <input
                    id="layer-label"
                    type="text"
                    value={newLayerData.label}
                    onChange={(e) => {
                      setNewLayerData({
                        ...newLayerData,
                        label: e.target.value,
                      });
                      if (validationError) {
                        setValidationError(null);
                      }
                    }}
                    className="bg-transparent text-[14px] text-[#444444] font-medium leading-normall placeholder-[#828282] border-none outline-none w-full"
                    placeholder="Enter layer display name"
                    maxLength={50}
                  />
                </div>
                <div className="flex justify-end mt-1">
                  <span
                    className={`text-[12px] font-medium ${newLayerData.label.length >= 50 ? "text-red-500" : "text-[#828282]"}`}
                  >
                    {newLayerData.label.length}/50
                  </span>
                </div>
              </div>

              <div>
                <Label.Root
                  htmlFor="service-type"
                  className="inline-flex gap-1 text-[14px] font-medium text-[#444444] mb-3"
                >
                  Service Type <span className="text-red-500">*</span>
                </Label.Root>
                <div className="bg-gray-100 rounded-[10px] px-4 py-3">
                  <select
                    id="service-type"
                    value={newLayerData.service_type}
                    onChange={(e) => {
                      const serviceType = e.target.value as
                        | "mapservice"
                        | "featureservice";
                      setNewLayerData({
                        ...newLayerData,
                        service_type: serviceType,
                        // Clear layer numbers when switching to feature service
                        layer_numbers:
                          serviceType === "featureservice"
                            ? []
                            : newLayerData.layer_numbers,
                      });
                      if (validationError) {
                        setValidationError(null);
                      }
                    }}
                    className="bg-transparent text-[14px] text-[#444444] font-medium leading-normal border-none outline-none w-full"
                  >
                    <option value="mapservice">Map Service (Dynamic)</option>
                    <option value="featureservice">Feature Service</option>
                  </select>
                </div>
                <p className="text-[12px] text-[#828282] mt-1">
                  <strong>Map Service:</strong> Better for display, supports
                  multiple sublayers, server-side rendering
                  <br />
                  <strong>Feature Service:</strong> Better for interaction,
                  client-side rendering, single layer per service
                </p>
              </div>

              <div>
                <Label.Root
                  htmlFor="layer-url"
                  className="inline-flex gap-1 text-[14px] font-medium text-[#444444] mb-3"
                >
                  Layer URL <span className="text-red-500">*</span>
                </Label.Root>
                <div className="bg-gray-100 rounded-[10px] px-4 py-3">
                  <input
                    id="layer-url"
                    type="text"
                    value={newLayerData.url}
                    onChange={(e) => {
                      setNewLayerData({
                        ...newLayerData,
                        url: e.target.value,
                      });
                      if (validationError) {
                        setValidationError(null);
                      }
                    }}
                    className="bg-transparent text-[14px] text-[#444444] font-medium leading-normall placeholder-[#828282] border-none outline-none w-full"
                    placeholder={
                      newLayerData.service_type === "featureservice"
                        ? "Enter ArcGIS Feature Service URL (e.g., .../FeatureServer/0)"
                        : "Enter ArcGIS Map Service URL (e.g., .../MapServer)"
                    }
                    maxLength={500}
                  />
                </div>
                <p className="text-[12px] text-[#828282] mt-1">
                  {newLayerData.service_type === "featureservice"
                    ? "Feature Service URLs should end with /FeatureServer/[layer_number]"
                    : "Map Service URLs should end with /MapServer"}
                </p>
              </div>

              {newLayerData.service_type === "mapservice" && (
                <div>
                  <Label.Root
                    htmlFor="layer-numbers"
                    className="inline-flex gap-1 text-[14px] font-medium text-[#444444] mb-3"
                  >
                    Layer Numbers <span className="text-red-500">*</span>
                  </Label.Root>
                  <div className="bg-gray-100 rounded-[10px] px-4 py-3">
                    <input
                      id="layer-numbers"
                      type="text"
                      value={newLayerData.layer_numbers.join(", ")}
                      onChange={(e) => {
                        const numbers = e.target.value
                          .split(",")
                          .map((n) => parseInt(n.trim()))
                          .filter((n) => !isNaN(n));
                        setNewLayerData({
                          ...newLayerData,
                          layer_numbers: numbers,
                        });
                        if (validationError) {
                          setValidationError(null);
                        }
                      }}
                      className="bg-transparent text-[14px] text-[#444444] font-medium leading-normal placeholder-[#828282] border-none outline-none w-full"
                      placeholder="Enter layer numbers (e.g., 5 or 4,5,6)"
                    />
                  </div>
                  <p className="text-[12px] text-[#828282] mt-1">
                    Comma-separated layer numbers from the ArcGIS service
                  </p>
                </div>
              )}

              <div className="flex items-center gap-6">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="layer-visible"
                    checked={newLayerData.visible}
                    onChange={(e) => {
                      setNewLayerData({
                        ...newLayerData,
                        visible: e.target.checked,
                      });
                    }}
                    className="w-4 h-4 rounded border border-gray-300"
                  />
                  <Label.Root
                    htmlFor="layer-visible"
                    className="text-[14px] font-medium text-[#444444] cursor-pointer"
                  >
                    Layer visible by default
                  </Label.Root>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="task-enabled"
                    checked={newLayerData.task_enabled}
                    onChange={(e) => {
                      setNewLayerData({
                        ...newLayerData,
                        task_enabled: e.target.checked,
                      });
                    }}
                    className="w-4 h-4 rounded border border-gray-300"
                  />
                  <Label.Root
                    htmlFor="task-enabled"
                    className="text-[14px] font-medium text-[#444444] cursor-pointer"
                  >
                    Enable task creation
                  </Label.Root>
                </div>
              </div>

              {/* Asset Fields Configuration */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <Label.Root className="text-[14px] font-medium text-[#444444]">
                    Asset Fields
                  </Label.Root>
                  <button
                    type="button"
                    onClick={addAssetField}
                    className="flex items-center gap-2 px-3 py-2 text-[12px] rounded-xl cursor-pointer font-medium border border-[#374795] text-[#374795] bg-[rgba(55,71,149,0.1)] hover:bg-[rgba(55,71,149,0.15)] transition-colors"
                  >
                    <TbPlus className="w-3 h-3" />
                    Add Field
                  </button>
                </div>

                {newLayerData.asset_fields &&
                  newLayerData.asset_fields.length > 0 && (
                    <div className="space-y-3  w-full">
                      {newLayerData.asset_fields.map(
                        (field: AssetFieldConfig, index: number) => (
                          <div
                            key={index}
                            className="flex flex-col gap-3 border border-gray-100 p-3 rounded-2xl w-full"
                          >
                            <div className="flex flex-row justify-between gap-2">
                              <div className="bg-gray-100 rounded-[10px] px-4 py-3">
                                <input
                                  type="text"
                                  value={field.field}
                                  onChange={(e) =>
                                    updateAssetField(index, {
                                      ...field,
                                      field: e.target.value,
                                    })
                                  }
                                  className="bg-transparent text-[12px] text-[#444444] font-medium leading-normall placeholder-[#828282] border-none outline-none w-full"
                                  placeholder="Field name (e.g., SFN)"
                                  maxLength={50}
                                />
                              </div>
                              <div className="bg-gray-100 rounded-[10px] px-4 py-3">
                                <input
                                  type="text"
                                  value={field.label}
                                  onChange={(e) =>
                                    updateAssetField(index, {
                                      ...field,
                                      label: e.target.value,
                                    })
                                  }
                                  className="bg-transparent text-[12px] text-[#444444] font-medium leading-normal placeholder-[#828282] border-none outline-none w-full"
                                  placeholder="Display label (e.g., Structure Number)"
                                  maxLength={50}
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => removeAssetField(index)}
                                className="cursor-pointer w-9 h-9 rounded-full border border-gray-300 bg-white flex items-center justify-center hover:bg-gray-50 transition-colors mt-0.5"
                              >
                                <TbTrash size={14} className="text-red-400" />
                              </button>
                            </div>
                            <div className="flex flex-row gap-7">
                              <div className="bg-gray-100 rounded-[10px] px-4 py-3">
                                <input
                                  type="text"
                                  value={field.property_key}
                                  onChange={(e) =>
                                    updateAssetField(index, {
                                      ...field,
                                      property_key: e.target.value,
                                    })
                                  }
                                  className="bg-transparent text-[12px] text-[#444444] font-medium leading-normal placeholder-[#828282] border-none outline-none w-full"
                                  placeholder="Property key (e.g., BIA_REPORT)"
                                  maxLength={50}
                                />
                              </div>
                              <div className="bg-gray-100 rounded-[10px] px-4 py-3">
                                <input
                                  type="text"
                                  value={field.extract_pattern || ""}
                                  onChange={(e) =>
                                    updateAssetField(index, {
                                      ...field,
                                      extract_pattern:
                                        e.target.value || undefined,
                                    })
                                  }
                                  className="bg-transparent text-[12px] text-[#444444] font-medium leading-normal placeholder-[#828282] border-none outline-none w-full"
                                  placeholder="Extract pattern (e.g., SFN=(\\d+))"
                                  maxLength={100}
                                />
                              </div>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  )}

                <p className="text-[12px] text-[#828282] mt-2">
                  Configure which fields from the ArcGIS layer should be
                  extracted as asset identifiers for task creation. Use extract
                  patterns (regex) to parse specific values from property
                  fields.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="skip-asset-allowed"
                  checked={newLayerData.skip_asset_allowed}
                  onChange={(e) => {
                    setNewLayerData({
                      ...newLayerData,
                      skip_asset_allowed: e.target.checked,
                    });
                  }}
                  className="w-4 h-4 rounded border border-gray-300"
                />
                <Label.Root
                  htmlFor="skip-asset-allowed"
                  className="text-[14px] font-medium text-[#444444] cursor-pointer"
                >
                  Allow skipping asset field selection
                </Label.Root>
              </div>
            </div>
          </div>
          <div className="flex gap-6 w-full">
            <button
              onClick={handleSubmit}
              disabled={
                createLayerMutation.isPending || updateLayerMutation.isPending
              }
              className="self-stretch w-full px-6 py-3 rounded-[10px] gap-2.5 text-white bg-[#374795] hover:bg-[#132477] cursor-pointer text-sm font-semibold font-['Open_Sans'] leading-tight disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createLayerMutation.isPending || updateLayerMutation.isPending
                ? "Saving..."
                : editingLayer
                  ? "Update Layer"
                  : "Create Layer"}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-col items-start w-full overflow-y-auto max-h-[calc(100vh-250px)] md:max-h-[calc(100vh-380px)]">
            <div className="space-y-4 w-full">
              {(error || fetchError) && (
                <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {error || "Failed to load layers"}
                </div>
              )}

              {/* Search Bar */}
              <div className="flex flex-col items-start gap-1.5 w-full rounded-lg">
                <div className="flex p-[13px_14px] items-center gap-4 w-full rounded-[10px] bg-[#F3F4F6]">
                  <div className="flex justify-center items-center gap-2 flex-1">
                    <TbSearch className="w-4 h-4 text-[#828282]" />
                    <input
                      type="text"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="bg-transparent border-none outline-none text-[#828282] font-['Open_Sans'] text-[12px] font-medium leading-[18px] flex-1 placeholder:text-[#828282]"
                    />
                  </div>
                </div>
              </div>

              {/* Layers List */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <div className="space-y-3">
                  {filteredLayers.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      {searchTerm
                        ? "No layers found matching your search."
                        : "No layers created yet. Create your first layer!"}
                    </div>
                  ) : (
                    <SortableContext
                      items={filteredLayers.map((layer) => layer.key)}
                      strategy={verticalListSortingStrategy}
                    >
                      {filteredLayers.map((layer: ArcgisLayerConfig) => (
                        <SortableLayerItem
                          key={layer.key}
                          layer={layer}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                          isAdmin={isAdmin}
                          onColorChange={(layerToUpdate, color) => {
                            const oldColor =
                              layerToUpdate.color || DEFAULT_TASK_LAYER_COLOR;
                            updateLayerMutation.mutate(
                              {
                                key: layerToUpdate.key,
                                data: { color },
                              },
                              {
                                onSuccess: () => {
                                  if (!tenantId) return;
                                  ActivityLogService.logTenantAction(
                                    tenantId,
                                    "update_layer_color",
                                    "asset_layer",
                                    layerToUpdate.key,
                                    {
                                      layer: layerToUpdate.label,
                                      from: oldColor,
                                      to: color,
                                    }
                                  )
                                    .catch((err) => {
                                      console.error(
                                        "Failed to write activity log for layer color change:",
                                        err
                                      );
                                    })
                                    .finally(() => {
                                      queryClient.invalidateQueries({
                                        queryKey: [
                                          "activity-logs",
                                          "tenant",
                                          tenantId,
                                        ],
                                      });
                                    });
                                },
                              }
                            );
                          }}
                          isColorSaving={updateLayerMutation.isPending}
                        />
                      ))}
                    </SortableContext>
                  )}
                </div>
              </DndContext>
            </div>
          </div>

          {/* Add New Layer Button */}
          {isAdmin && (
            <div className="flex gap-6 w-full mt-4">
              <button
                onClick={handleStartCreateLayer}
                className="self-stretch w-full px-6 py-3 rounded-[10px] gap-2.5 text-white bg-[#374795] hover:bg-[#132477] cursor-pointer text-sm font-semibold font-['Open_Sans'] leading-tight flex items-center justify-center"
              >
                <TbPlus className="w-4 h-4" />
                Add New Layer
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
